/* eslint-disable react-hooks/exhaustive-deps */
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opacity2hex, setNumberFormat, sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { getCurrenyTableColumns } from "utils/tableHelper/extraTableHelper";
import { deleteCurrency, getCurrencies } from "services/currencyService";
import CustomButton from "components/CustomButton";
import { defaultPageNo } from "utils/constants";
import useSearchHook from "hooks/useSearch";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";

const useStyles = makeStyles((theme) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
}));

const RowActions = ({ rowId, data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.currency.edit?.replace(":id", data?._id));
  };

  const handleDeleteClick = (event: any, data: any) => {
    handleClose(event);
    actionHandlers.handleDelete(data);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.CURRENCY.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={(e) => handleRowAction(e, data)}>Edit</MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={(e) => handleDeleteClick(e, data)}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const Currency = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.CURRENCY.UPDATE_ROLES,
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const createCurrency = () => navigate(configData.routes.currency.create);

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.currency.edit?.replace(":id", data?._id)
      : null;

  const fetchData = async (_search?: any) => {
    setLoading(true);
    try {
      // feth leagues data
      let payload = { pageNo, pageSize };
      if (searchValue) payload["searchValue"] = searchValue;
      const resp = await getCurrencies(payload);
      setFilteredList(
        resp?.data?.currencies?.map((currency) => ({
          ...currency,
          minBet: setNumberFormat(currency.minBet || 0, 2),
          maxBet: setNumberFormat(currency.maxBet || 0, 2),
          maxPayOut: setNumberFormat(currency.maxPayOut || 0, 2),
          gbpExchangeRate: setNumberFormat(currency.gbpExchangeRate || 0, 2),
        })),
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.isoCode, id: data?._id });
  };

  const onDelete = async (currencyId) => {
    try {
      const resp = await deleteCurrency(currencyId);
      showSnackbar(resp?.message);
      fetchData(searchValue);
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onSearch = (value: any) => {
    onChangePage(defaultPageNo);
    _onSearch(value);
  };

  useEffect(() => {
    const cols = getCurrenyTableColumns();
    setColumns(cols.filter((col) => col.id !== "actions" || hasEditPermission));
    fetchData();
  }, [pageNo, pageSize, searchValue]);

  return (
    <ContentShell
      title="Currencies"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a currency"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.CURRENCY.CREATE_ROLES) && (
            <CustomButton onClick={createCurrency}>Add</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          highlightAltRow={true}
          actions={RowActions}
          rowHref={rowHref}
          onColumnClick={onColumnClick}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{ handleDelete: _openConsentDialog }}
        />

        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default Currency;
